<template>
  <button
    v-if="$auth.loggedIn"
    type="button"
    class="header-commission-balance"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <transition name="fade" @after-enter="onAnimationCompleted">
      <HeaderCommissionBalanceBadge
        v-if="shouldAnimate"
        :count="commissionsDelta"
        class="header-commission-balance__badge"
      />
    </transition>
    <ScoreIcon width="16" height="16" class="header-commission-balance__icon" :style="scoreIconStyle" />
    <span
      ref="counter"
      class="header-commission-balance__counter"
      data-test="counter-block"
      :style="counterStyles"
    ></span>
  </button>
</template>

<script lang="ts">
import type { CountUp } from 'countup.js';
import { defineComponent } from '@nuxtjs/composition-api';
import ScoreIcon from '@/assets/svg/score.vue.svg';
import HeaderCommissionBalanceBadge from '@/components/global/Header/HeaderCommissionBalance/HeaderCommissionBalanceBadge.vue';
import CommissionOnboardingQuery from '@/apollo/queries/CommissionOnboarding.query';
import HeaderCommissionBalanceMixin from '@/components/global/Header/HeaderCommissionBalance/HeaderCommissionBalanceMixin';

const ICON_ROTATE_ANGLE = 90;
const COUNTER_ANIMATION_DELAY_MS = 1000;
const COUNTER_ANIMATION_DURATION_S = 1.4;

export default defineComponent({
  name: 'HeaderCommissionBalance',
  components: {
    ScoreIcon,
    HeaderCommissionBalanceBadge,
  },
  mixins: [HeaderCommissionBalanceMixin],
  data(): { isTabFocused: boolean; scoreIconAngle: number; animationCompleted: boolean; counter: CountUp | null } {
    return {
      isTabFocused: false,
      scoreIconAngle: 0,
      animationCompleted: false,
      counter: null,
    };
  },
  computed: {
    commissionsDelta: {
      get() {
        return this.commissionBalance?.delta ?? 0;
      },
      set(newValue: number) {
        if (this.commissionBalance) this.commissionBalance.delta = newValue;
      },
    },
    commissionsBalance() {
      return (this.commissionBalance?.currentBalance ?? 0) - this.commissionsDelta;
    },
    shouldAnimate() {
      return this.commissionsDelta !== 0 && this.isTabFocused;
    },
    isCurrentDeltaPositive() {
      return (this.commissionsDelta ?? 0) > 0;
    },
    scoreIconStyle() {
      return { transform: `rotate(${this.scoreIconAngle}deg)` };
    },
    counterStyles() {
      return { minWidth: `calc(${this.commissionsBalance.toString().length}ch + 2px)` };
    },
  },
  watch: {
    shouldAnimate(newVal: boolean) {
      if (!newVal) return;

      this.animationCompleted = false;
      this.scoreIconAngle = this.isCurrentDeltaPositive
        ? this.scoreIconAngle + ICON_ROTATE_ANGLE
        : this.scoreIconAngle - ICON_ROTATE_ANGLE;
      setTimeout(() => {
        this.counter?.update(this.commissionsBalance + (this?.commissionsDelta ?? 0));
      }, COUNTER_ANIMATION_DELAY_MS);
    },
    commissionsBalance: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.animateCounter();
        }
      },
    },
  },
  mounted() {
    if (!document.hidden) {
      this.isTabFocused = true;
    }

    window.onfocus = () => {
      this.isTabFocused = true;
    };

    window.onblur = () => {
      this.isTabFocused = false;
    };

    this.subscribe();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    async animateCounter() {
      const { CountUp } = await import('countup.js');
      const counterOptions = {
        startVal: this.commissionsBalance,
        duration: COUNTER_ANIMATION_DURATION_S,
        separator: ',',
      };
      this.counter = new CountUp(this.$refs.counter as HTMLElement, 0, counterOptions);
    },
    onAnimationCompleted() {
      if (!this.$auth.loggedIn) {
        return;
      }

      this.$api
        .post(`/api/war_room/user/commission_transactions/mark_as_shown`)
        .then(() => {
          this.commissionsDelta = 0;
          return this.$apollo.getClient().queryManager.watchQuery({ query: CommissionOnboardingQuery }).refetch();
        })
        .then(() => {
          this.animationCompleted = true;
        });
    },
    handleClick() {
      this.$analytics.logEvent('war-room__common__header-score__click');
      this.$router.push({ name: 'war-room-products' });
    },
  },
});
</script>

<style scoped lang="scss">
$transition-delay: 1.2s;
$transition-duration: 0.7s;

.header-commission-balance {
  @include body-semibold;
  @include unstyled-button;

  text-decoration: none;
  margin-left: 15px;
  display: flex;
  align-items: center;
  color: var(--theme-color-text);
  position: relative;
  cursor: pointer;

  &__icon {
    margin: 0 2px 0 0;
    will-change: transform;
    transition: transform $transition-duration ease;
    transition-delay: $transition-delay;
  }

  &__icon--rotated {
    transform: rotate(90deg);
  }

  &__icon--rotated-negative {
    transform: rotate(-90deg);
  }

  &__badge {
    will-change: transform;
    position: relative;
    z-index: 1;
    backface-visibility: hidden;
    transform: translateX(-6px) translate3d(0, 0, 0);
  }

  .fade-enter-active {
    transition: all $transition-duration ease;
    transition-delay: $transition-delay;
  }

  .fade-leave-active {
    transition: all $transition-duration ease;
  }

  .fade-enter,
  .fade-leave-to {
    transform: translateX(-12px);
    opacity: 0;
  }

  &__counter {
    will-change: min-width;

    @include body-semibold;

    transition: min-width $transition-duration ease;
    text-align: center;
  }
}
</style>
